import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { useMemo } from 'react';
import { FF_BATCH_MERGE_DIAGNOSTICS, FF_DETERMINISTIC_BATCHES_MERGE } from '@/consts/featureFlags';
import { log } from '@/logger/logger';
import { posthog } from '@/providers/posthog';
import { getBatchesForNode, isAnyRun, mergeBatches } from './batch.utils';
import type { BatchRun, NodeRun, RecipeRun } from '@/types/batch.types';

const logger = log.getLogger('batches.store');

export type BatchesState = {
  batches: Record<string, BatchRun>;
  updateBatches: (batches: Record<string, BatchRun>) => void;
  isTaskManagerOpen: boolean;
  setIsTaskManagerOpen: (isOpen: boolean) => void;
  clearBatch: (bid: string) => void;
  clearAllBatches: () => void;
  markBatchesAsViewed: () => void;
};

const getNodeRunDiffFields = ({ progress, id, runId, nodeId, status }: NodeRun) => ({
  progress,
  id,
  runId,
  nodeId,
  status,
});

const getRecipeRunDiffFields = ({ inputIndex, id, runIndexInBatch, batchId, status, nodeRuns }: RecipeRun) => ({
  inputIndex,
  id,
  runIndexInBatch,
  batchId,
  status,
  nodeRuns: nodeRuns.map(getNodeRunDiffFields),
});

const getBatchDiffFields = ({ batchId, remainingCredits, userRemainingCredits, recipeId, recipeRuns }: BatchRun) => ({
  batchId,
  remainingCredits,
  userRemainingCredits,
  recipeId,
  recipeRuns: recipeRuns.map(getRecipeRunDiffFields),
});

const getDifferingBatchIds = (
  mergedBatches: Record<string, BatchRun>,
  preservedBatches: Record<string, BatchRun>,
  incomingBatchIds: string[],
): string[] =>
  incomingBatchIds.filter(
    (bid) => !isEqual(getBatchDiffFields(mergedBatches[bid]), getBatchDiffFields(preservedBatches[bid])),
  );

export const useBatchesStore = create<BatchesState>()(
  devtools(
    (set) => ({
      isTaskManagerOpen: false,
      setIsTaskManagerOpen: (isOpen: boolean) => set({ isTaskManagerOpen: isOpen }),
      batches: {},
      updateBatches: (batches: Record<string, BatchRun>) =>
        set((state) => {
          const legacyMergedBatches = merge({}, state.batches, batches);
          const deterministicMergedBatches = mergeBatches(state.batches, batches);
          const mergedBatches =
            posthog.isFeatureEnabled(FF_DETERMINISTIC_BATCHES_MERGE) === true
              ? deterministicMergedBatches
              : legacyMergedBatches;
          if (posthog.isFeatureEnabled(FF_BATCH_MERGE_DIAGNOSTICS) === true) {
            try {
              const differingBatchIds = getDifferingBatchIds(
                legacyMergedBatches,
                deterministicMergedBatches,
                Object.keys(batches),
              );

              if (differingBatchIds.length > 0) {
                logger.warn('updateBatches: lodash merge differs from new batch merge', {
                  incomingBatches: batches,
                  differingBatchIds,
                  mergedBatches: Object.fromEntries(differingBatchIds.map((bid) => [bid, legacyMergedBatches[bid]])),
                  preservedBatches: Object.fromEntries(
                    differingBatchIds.map((bid) => [bid, deterministicMergedBatches[bid]]),
                  ),
                });
              }
            } catch (error) {
              logger.error('updateBatches: error batches diagnostics failed', { error });
            }
          }

          return { batches: mergedBatches };
        }),
      clearBatch: (bid: string) =>
        set((state) => ({
          batches: { ...state.batches, [bid]: { ...state.batches[bid], isClearedInTaskManager: true } },
        })),
      clearAllBatches: () =>
        set((state) => ({
          batches: Object.fromEntries(
            Object.entries(state.batches).map(([bid, batch]) => [
              bid,
              {
                ...batch,
                isClearedInTaskManager: !isAnyRun(batch, 'RUNNING'),
              },
            ]),
          ),
        })),
      markBatchesAsViewed: () =>
        set((state) => ({
          batches: Object.fromEntries(
            Object.entries(state.batches).map(([bid, batch]) => [bid, { ...batch, isViewed: true }]),
          ),
        })),
    }),
    {
      name: 'Batches Store',
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useBatches = (): Record<string, BatchRun> => {
  return useBatchesStore((state) => state.batches);
};

export const useUpdateBatches = (): ((batches: Record<string, BatchRun>) => void) => {
  return useBatchesStore((state) => state.updateBatches);
};

export const useBatchesByRecipeId = (recipeId: string): BatchRun[] => {
  const batches = useBatches();
  const recipeBatches = useMemo(
    () => Object.values(batches).filter((batch) => batch.recipeId === recipeId),
    [batches, recipeId],
  );
  return recipeBatches;
};

export const useGetActiveBatches = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(() => Object.values(batches).filter((batch) => isAnyRun(batch, 'RUNNING')), [batches]);
};

export const useGetActiveBatchesWithActiveNode = (nodeId: string): BatchRun[] => {
  const batches = useRunningBatchesByNodeId(nodeId);
  return useMemo(
    () =>
      batches.filter((batch) =>
        batch.recipeRuns.some((run) =>
          run.nodeRuns.some(
            (nodeRun) => nodeRun.nodeId === nodeId && (nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING'),
          ),
        ),
      ),
    [batches, nodeId],
  );
};

export const useBatchesByNodeId = (nodeId: string): BatchRun[] => {
  const batches = useBatches();
  return useMemo(() => getBatchesForNode(nodeId, Object.values(batches)), [batches, nodeId]);
};

export const useRunningBatchesByNodeId = (nodeId: string): BatchRun[] => {
  const batches = useBatchesByNodeId(nodeId);
  return useMemo(() => batches.filter((batch) => isAnyRun(batch, 'RUNNING')), [batches]);
};

export const useIsNodeInRunningBatch = (nid: string): boolean => {
  const batches = useRunningBatchesByNodeId(nid);
  return useMemo(() => batches.length > 0, [batches]);
};

export const useRunningBatchesByRecipeId = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(() => batches.filter((batch) => isAnyRun(batch, 'RUNNING')), [batches]);
};

export const useAnyBatchDone = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(
    () =>
      batches.filter((batch) =>
        batch.recipeRuns.every(
          (run) => run.status === 'COMPLETED' || run.status === 'FAILED' || run.status === 'CANCELED',
        ),
      ),
    [batches],
  );
};
